import { Temporal } from 'temporal-polyfill'
import { DomainError } from './errors'

export { Temporal }

/** Every business date and time of day is in this zone; storage is UTC (ARCHITECTURE). */
export const BUSINESS_TIME_ZONE = 'Europe/Rome'

const LOCAL_TIME = /^([01]\d|2[0-3]):([0-5]\d)$/
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

export function toInstant(date: Date): Temporal.Instant {
  const ms = date.getTime()
  if (Number.isNaN(ms)) throw new DomainError('invalid_time', 'not a valid date-time')
  return Temporal.Instant.fromEpochMilliseconds(ms)
}

export function fromInstant(instant: Temporal.Instant): Date {
  return new Date(instant.epochMilliseconds)
}

/** "HH:MM", 24-hour clock. */
export function parseLocalTime(value: string): Temporal.PlainTime {
  const match = LOCAL_TIME.exec(value)
  if (!match) throw new DomainError('invalid_time', 'expected a local time as HH:MM', { value })
  return new Temporal.PlainTime(Number(match[1]), Number(match[2]))
}

/** "YYYY-MM-DD", a real calendar date. */
export function parseLocalDate(value: string): Temporal.PlainDate {
  if (!ISO_DATE.test(value)) {
    throw new DomainError('invalid_date', 'expected a date as YYYY-MM-DD', { value })
  }
  try {
    return Temporal.PlainDate.from(value, { overflow: 'reject' })
  } catch {
    throw new DomainError('invalid_date', 'not a calendar date', { value })
  }
}

export function toBusinessDateTime(date: Date): Temporal.ZonedDateTime {
  return toInstant(date).toZonedDateTimeISO(BUSINESS_TIME_ZONE)
}

/** The Europe/Rome calendar date of an instant, as YYYY-MM-DD. */
export function businessDateOf(date: Date): string {
  return toBusinessDateTime(date).toPlainDate().toString()
}

/**
 * The instant at which a Europe/Rome wall-clock time occurs on a date. A time
 * that does not exist (the spring-forward gap) moves forward by the gap; a time
 * that occurs twice (the autumn fall-back) resolves to the first occurrence.
 */
export function atBusinessTime(
  date: Temporal.PlainDate | string,
  time: Temporal.PlainTime | string,
): Date {
  const plainDate = typeof date === 'string' ? parseLocalDate(date) : date
  const plainTime = typeof time === 'string' ? parseLocalTime(time) : time
  const zoned = plainDate.toZonedDateTime({ timeZone: BUSINESS_TIME_ZONE, plainTime })
  return fromInstant(zoned.toInstant())
}

/** The instants a business day starts and ends at (23, 24 or 25 hours long). */
export function businessDay(date: string): { start: Date; end: Date } {
  const day = parseLocalDate(date)
  return { start: atBusinessTime(day, '00:00'), end: atBusinessTime(day.add({ days: 1 }), '00:00') }
}
